import { Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { environment } from 'src/environments/environment';
import { NotificationSnackComponent } from '../shared/notification-snack/notification-snack.component';
import { HttpClient } from '@angular/common/http';
import { ApiBaseService } from './api.base.service';
import { Observable, of } from 'rxjs';
import { userServices } from './users.service';
import { ImportJobService } from './importJobService';
import { activityTypeCache } from './cache/activity-types-cache';
import { CampusCacheService } from './cache/campus-cache';
import { UsersCacheService } from './cache/users-cache';
import { TagsCacheService } from './cache/tags-cache';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService extends ApiBaseService {
  private audio: HTMLAudioElement;
  private _total_notifications = signal(0);

  constructor(
    private snackBar: MatSnackBar,
    private messaging: Messaging,
    public http: HttpClient,
    private usersService: userServices,
    private importJobService: ImportJobService,
    private activityTypeCache: activityTypeCache,
    private campusCacheService:CampusCacheService,
    private usersCacheService:UsersCacheService,
    private TagsCacheService:TagsCacheService
  ) {
    super(http, '/notifications');
    this.audio = new Audio('assets/audio/notification.mp3');

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/firebase-messaging-sw.js').then(
        (registration) => {
        },
        (err) => {
          console.error('Service Worker registration failed:', err);
        }
      );

      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === "NO_NEW_VERSION_DETECTED" || event.data?.payload?.type === "NO_NEW_VERSION_DETECTED") {
          return;
        }
        if (event.data?.version && !event.data?.version?.appData) {
          return;
        }
        if (!event.data ||
          typeof event.data !== 'object' ||
          !event.data.data ||
          !event.data.notification) {
          return;
        }

        const messageData = event.data.payload || event.data;
        const notificationType = messageData.notification?.type;
      
        // Route to appropriate handler based on notification type
        switch (notificationType) {
          case 'import_job':
            this.handleImportJob(messageData);
            break;
          case 'activity-type':
            this.handleEntityChanges(event, this.activityTypeCache, 'activityTypesObj');
            break;
          case 'campuses':
            this.handleEntityChanges(event, this.campusCacheService, 'campusObj');
            break;
          case 'users':
            const userData = JSON.parse(event?.data?.data?.userObj);
            if (messageData.notification.method === 'post') {
              const { Company, ...userWithoutCompany } = userData;
              this.usersCacheService.createLocal(userWithoutCompany);
            } else {
              this.handleEntityChanges(event, this.usersCacheService, 'userObj');
            }
            break;
          case 'tags':
            this.handleEntityChanges(event, this.TagsCacheService, 'tagsObj');
            break;
          default:
            this.handleNotification(event);
        }
      }
      )
    }
  }

  async handleImportJob(messageData) {
    this.importJobService.updateJobStatus({
      jobId: messageData.data.jobId,
      progress: parseInt(messageData.data.progress, 10) || 0,
      successCount: parseInt(messageData.data.successCount, 10) || 0,
      failedCount: parseInt(messageData.data.failedCount, 10) || 0,
      status: messageData.data.status as 'in_progress' | 'success' | 'failed',
      message: messageData.notification.body
    });
  }

  async handleNotification(event) {
    this.playNotificationSound();
    this.increment(1);
    this.snackBar.openFromComponent(NotificationSnackComponent, {
      duration: 10000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: {
        message: event.data.payload || event.data, // Pass data here
        additionalInfo: 'You can pass more data as needed',
      },
    });
  }

  async handleEntityChanges(event, cacheService, dataKey) {
    const data = JSON.parse(event?.data?.data?.[dataKey])
    const notification = event?.data?.notification
    const method = notification.method
    switch (method) {
      case 'post':
      case 'reverse':
        await cacheService.createLocal(data)
        break
      case 'put':
        await cacheService.updateLocal(data, data.id)
        break
      case 'delete':
        await cacheService.deleteLocal(data.id)
        break
      case 'sequenceChange':
        if (cacheService.sequenceUpdate) {
          await cacheService.sequenceUpdate(data.previousIndex, data.currentIndex)
        }
        break
    }
  }

  getTotalUnseen(): Observable<any> {
    return this.http.get(`${environment.apiUrl}/notifications/unseen`);
  }


  markAsSeen(data): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/notifications/markAsSeen`, data);
  }

  getDeviceToken(): void {
    getToken(this.messaging, { vapidKey: environment.vapidKey })
      .then((token) => {
        // Retrieve browser and tab information
        const browserInfo = {
          userAgent: navigator.userAgent, // Browser user agent string
          browserLanguage: navigator.language, // Language setting of the browser
          platform: navigator.platform, // Operating system platform
          title: document.title, // Current tab title
          url: window.location.href, // Current tab URL
        };
        
        // Assuming user data is available in localStorage
        const userData = localStorage.getItem('userData');
        if (userData) {
          this.usersService.saveToken(token, JSON.parse(userData).id).subscribe();
        }
      })
      .catch((error) => console.error('Token error:', error));
  }

  private playNotificationSound(): void {
    this.audio
      .play()
      .catch((err) => console.error('Error playing audio: ', err));
  }

  initializeAudio(): void {
    // This method must be called after user interaction
    this.audio.load(); // Pre-loads the audio
  }


  notification_count() {
    return this._total_notifications();
  }

  increment(value: number) {
    this._total_notifications.update(count => count + value);
  }

  decrement() {
    this._total_notifications.update(count => Math.max(0, count - 1));
  }

  set(value: number) {
    this._total_notifications.set(value);
  }
}
